import type { weighIn } from "../types/weighIn";
import type {
  CreateComposedMealRequest,
  CreateIngredientResponse,
  CreateMealRawRequest,
  CreateNewIngredientRequest,
  CreateRecipeRequest,
  CreateRecipeResponse,
  GetIngredientsResponse,
  GetIngredientFromImageResponse,
  GetMealHistoryExistsResponse,
  GetMealHistoryResponse,
  GetPantryExportResponse,
  GetRecipesResponse,
  GetWeighInDataResponse,
  ImportPantryRequest,
  ImportPantryResponse,
  PatchRecipeRequest,
  PatchRecipeResponse,
  ResetRecipeResponse,
} from "@macro-tracker/macro-tracker-shared";
import {
  INGREDIENT_IMAGE_FIELD_NAME,
  MacroData,
  WeighInData,
  User,
} from "@macro-tracker/macro-tracker-shared";

export type APIResult<T> =
  | {
      ok: true;
      status: number;
      body: T;
    }
  | {
      ok: false;
      status: number;
      errorMessage: string;
    };

async function parseErrorMessage(
  apiResult: Response,
  fallback: string,
): Promise<string> {
  let errorMessage = fallback;
  try {
    const json: unknown = await apiResult.json();
    if (
      typeof json === "object" &&
      json !== null &&
      "error" in json &&
      typeof (json as { error: unknown }).error === "string"
    ) {
      errorMessage = (json as { error: string }).error;
    } else if (
      typeof json === "object" &&
      json !== null &&
      "errorMessage" in json &&
      typeof (json as { errorMessage: unknown }).errorMessage === "string"
    ) {
      errorMessage = (json as { errorMessage: string }).errorMessage;
    }
  } catch {
    /* keep default */
  }
  return errorMessage;
}

export async function getMealHistoryFromRange(
  fromDate: Date,
  toDate: Date,
): Promise<APIResult<GetMealHistoryResponse>> {
  const searchParams = new URLSearchParams({
    fromDate: fromDate.toISOString().split("T")[0],
    toDate: toDate.toISOString().split("T")[0],
  });

  const apiResult = await fetch(
    `/api/meals/history?${searchParams.toString()}`,
  );

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: await apiResult.json(),
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Unable to get meal history",
    };
  }
}

export async function hasMealHistoryBefore(
  beforeDate: Date,
): Promise<APIResult<GetMealHistoryExistsResponse>> {
  const searchParams = new URLSearchParams({
    beforeDate: beforeDate.toISOString().split("T")[0],
  });

  const apiResult = await fetch(
    `/api/meals/history/exists?${searchParams.toString()}`,
  );

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: await apiResult.json(),
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Unable to get meal history",
    };
  }
}

export async function getTodaysMacros(today: Date) {
  const searchParams = new URLSearchParams({
    today: today.toISOString().split("T")[0],
  });

  const apiResult = await fetch(`/api/macros/today?${searchParams.toString()}`);

  if (apiResult.ok) {
    const jsonResult = await apiResult.json();
    return new MacroData(
      jsonResult.date,
      jsonResult.calories,
      jsonResult.protein,
      jsonResult.carbohydrates,
      jsonResult.fats,
    );
  } else {
    throw new Error("Unable to get current macro totals");
  }
}

export async function getUserLogout() {
  const apiResult = await fetch(`/api/users/logout`);
  if (!apiResult.ok) throw new Error("An error occurred while logging out.");
}

export async function postCreateNewUser(
  username: string,
  password: string,
  confirmedPassword: string,
  email: string,
) {
  const apiResult = await fetch(`/api/users/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      username: username,
      password: password,
      passwordConfirm: confirmedPassword,
      emailAddress: email,
    }),
  });

  if (apiResult.ok) {
    return User.fromJson(await apiResult.json());
  } else if (apiResult.status === 400) {
    const jsonResult = await apiResult.json();
    throw new Error(jsonResult.errorMessage);
  } else {
    throw new Error("An error occurred while attempting to create a user.");
  }
}

export async function postUserLogin(
  username: string,
  password: string,
  rememberMe: boolean,
) {
  const apiResult = await fetch(`/api/users/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      username: username,
      password: password,
      rememberMe: rememberMe,
    }),
  });

  if (apiResult.ok) {
    const jsonResult = await apiResult.json();
    return User.fromJson(jsonResult);
  } else if (apiResult.status === 400) {
    const jsonResult = await apiResult.json();
    throw new Error(jsonResult.errorMessage);
  } else {
    throw new Error("An error occurred while attempting to log in.");
  }
}

export async function deleteMeal(mealId: number) {
  const apiResult = await fetch(`/api/meals/${mealId}`, {
    method: "DELETE",
  });

  if (apiResult.ok) {
    return true;
  } else {
    return false;
  }
}

export async function putMealRecurring(mealId: number, isRecurring: boolean) {
  const apiResult = await fetch(`/api/meals/${mealId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isRecurring: isRecurring }),
  });

  return apiResult.ok;
}

export async function getIngredientFromImage(
  file: File,
): Promise<APIResult<GetIngredientFromImageResponse>> {
  const formData = new FormData();
  formData.append(INGREDIENT_IMAGE_FIELD_NAME, file);

  const apiResult = await fetch(`/api/ingredients/fromImage`, {
    method: "POST",
    body: formData,
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as GetIngredientFromImageResponse,
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: await parseErrorMessage(
        apiResult,
        "Failed to process image.",
      ),
    };
  }
}

export async function getIngredients(): Promise<
  APIResult<GetIngredientsResponse>
> {
  const apiResult = await fetch(`/api/ingredients`);

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as GetIngredientsResponse,
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Unable to get ingredients",
    };
  }
}

export async function createNewIngredient(
  request: CreateNewIngredientRequest,
): Promise<APIResult<CreateIngredientResponse>> {
  const apiResult = await fetch(`/api/ingredients`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as CreateIngredientResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(
      apiResult,
      "Unable to create ingredient",
    ),
  };
}

export async function deleteIngredient(
  ingredientId: number,
): Promise<APIResult<null>> {
  const apiResult = await fetch(`/api/ingredients/${ingredientId}`, {
    method: "DELETE",
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: null,
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: await parseErrorMessage(
        apiResult,
        "Unable to delete ingredient",
      ),
    };
  }
}

export async function getRecipes(): Promise<APIResult<GetRecipesResponse>> {
  const apiResult = await fetch(`/api/recipes`);

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as GetRecipesResponse,
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Unable to get recipes",
    };
  }
}

export async function createRecipe(
  request: CreateRecipeRequest,
): Promise<APIResult<CreateRecipeResponse>> {
  const apiResult = await fetch(`/api/recipes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as CreateRecipeResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to create recipe"),
  };
}

export async function patchRecipe(
  recipeId: number,
  request: PatchRecipeRequest,
): Promise<APIResult<PatchRecipeResponse>> {
  const apiResult = await fetch(`/api/recipes/${recipeId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as PatchRecipeResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to update recipe"),
  };
}

export async function resetRecipe(
  recipeId: number,
): Promise<APIResult<ResetRecipeResponse>> {
  const apiResult = await fetch(`/api/recipes/${recipeId}/reset`, {
    method: "POST",
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as ResetRecipeResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to reset recipe"),
  };
}

export async function deleteRecipe(recipeId: number): Promise<APIResult<null>> {
  const apiResult = await fetch(`/api/recipes/${recipeId}`, {
    method: "DELETE",
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: null,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to delete recipe"),
  };
}

export async function exportPantry(): Promise<
  APIResult<GetPantryExportResponse>
> {
  const apiResult = await fetch(`/api/pantry/export`);

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as GetPantryExportResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to export pantry"),
  };
}

export async function importPantry(
  request: ImportPantryRequest,
): Promise<APIResult<ImportPantryResponse>> {
  const apiResult = await fetch(`/api/pantry/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as ImportPantryResponse,
    };
  }

  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(apiResult, "Unable to import pantry"),
  };
}

export async function getWeighInDataFromRange(
  fromDate: Date,
  toDate: Date,
): Promise<APIResult<GetWeighInDataResponse>> {
  const searchParams = new URLSearchParams({
    fromDate: fromDate.toISOString().split("T")[0],
    toDate: toDate.toISOString().split("T")[0],
  });

  const apiResult = await fetch(`/api/weighIn?${searchParams.toString()}`);

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as GetWeighInDataResponse,
    };
  } else {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Unable to get weigh in history",
    };
  }
}

export async function getMostRecentWeighIn(): Promise<APIResult<WeighInData>> {
  const apiResult = await fetch(`/api/weighIn/recent`);

  if (!apiResult.ok) {
    return {
      ok: false,
      status: apiResult.status,
      errorMessage: "Could not retrieve weigh in data.",
    };
  } else {
    const tmp = WeighInData.fromRecentJSON(await apiResult.json());

    return {
      ok: true,
      status: apiResult.status,
      body: tmp,
    };
  }
}

export async function postWeighIn(weighInData: weighIn) {
  const apiResult = await fetch(`/api/weighIn`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(weighInData),
  });

  if (!apiResult.ok) throw new Error("Error sending new macro targets.");
}

export async function postMealNonComposed(newMeal: CreateMealRawRequest) {
  const apiResult = await fetch(`/api/meals/nonComposed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(newMeal),
  });

  if (apiResult.ok) {
    return await apiResult.json();
  } else if (apiResult.status === 400) {
    const jsonResult = await apiResult.json();
    throw new Error(jsonResult.error);
  } else {
    throw new Error("There was a problem adding the new meal.");
  }
}

/** Composed meal (`POST /api/meals`). Normalizes `is_recurring` to `isRecurring` for UI state. */
export async function postMealComposed(meal: CreateComposedMealRequest) {
  const apiResult = await fetch(`/api/meals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(meal),
  });

  if (apiResult.ok) {
    const json = (await apiResult.json()) as Record<string, unknown>;
    const { is_recurring: isRecurringSnake, ...rest } = json;
    return {
      ...rest,
      isRecurring: Boolean(isRecurringSnake),
    };
  } else if (apiResult.status === 400) {
    const jsonResult = await apiResult.json();
    throw new Error(
      typeof jsonResult.error === "string"
        ? jsonResult.error
        : "Invalid meal request",
    );
  } else {
    throw new Error("There was a problem adding the new meal.");
  }
}

export async function getPasswordRecovery(
  username: string,
): Promise<APIResult<null>> {
  const apiResult = await fetch(`/api/users/passwordRecovery/${username}`, {
    method: "POST",
  });
  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: null,
    };
  }
  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(
      apiResult,
      "Unable to set password recovery token.",
    ),
  };
}

export async function postVerificationToken(
  username: string,
  verificationToken: string,
): Promise<APIResult<{ verificationUuid: string }>> {
  const apiResult = await fetch(
    `/api/users/passwordRecovery/${username}/verificationToken`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ verificationToken }),
    },
  );
  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: (await apiResult.json()) as { verificationUuid: string },
    };
  }
  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(
      apiResult,
      "Unable to verify verification token",
    ),
  };
}

export async function postNewPassword(
  username: string,
  verificationUuid: string,
  newPassword: string,
  confirmNewPassword: string,
): Promise<APIResult<null>> {
  const apiResult = await fetch(
    `/api/users/passwordRecovery/${username}/newPassword`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        newPassword,
        confirmNewPassword,
        verificationUuid,
      }),
    },
  );

  if (apiResult.ok) {
    return {
      ok: true,
      status: apiResult.status,
      body: null,
    };
  }
  return {
    ok: false,
    status: apiResult.status,
    errorMessage: await parseErrorMessage(
      apiResult,
      "Unable to set new password",
    ),
  };
}
